import type { FC } from 'react';
import { ArrowUpDown, Plus, Search, Dot, Loader2, AlertCircle, RefreshCw, X } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
} from '@zawarski/eti-ui';
import { EmptyState, HeaderSection, ListCard } from '@/components/fragment';
import { PersonnelModal } from '@/components/modals';
import { Badge } from '@/components/ui/badge';
import { PageMaxLayout } from '@/components/layout';
import { useRootDimensions } from '@/hooks/useRootDimensions';
import { useIsMobile } from '@/hooks/use-mobile';
import type { PersonnelProps } from './Personnel.props';

const SORT_OPTIONS: { field: 'name' | 'updated_at'; order: 'asc' | 'desc'; label: string }[] = [
  { field: 'name', order: 'asc', label: 'Alphabetical' },
  { field: 'updated_at', order: 'desc', label: 'Last Updated' },
];

const PersonnelView: FC<PersonnelProps> = ({
  personnel,
  totalCount,
  groupNameMap,
  searchTerm,
  onSearchChange,
  isSearching,
  sortField,
  onSortChange,
  onRowClick,
  selectedPersonnel,
  isLoadingPersonnel,
  modalMode,
  isModalOpen,
  onAddClick,
  onModalClose,
  onSaveSuccess,
  isLoading,
  isLoadingMore,
  hasMore,
  error,
  onRetry,
  sentinelRef,
}) => {
  const dimensions = useRootDimensions();
  const isMobile = useIsMobile();

  return (
    <div className='h-full w-full'>
      {/* Header Section */}
      <HeaderSection
        title='Personnel'
        customBadge={
          <Badge variant='secondary' className='bg-card text-card-foreground'>
            {totalCount} entries
          </Badge>
        }
        tableSearchNode={
          <div className='flex items-center gap-4'>
            <div className='relative max-w-md flex-1'>
              <Input
                value={searchTerm}
                onChange={(ev) => onSearchChange(ev.target.value)}
                startAdornment={<Search className='text-foreground size-4 opacity-60' />}
                endAdornment={
                  !isSearching && (
                    <X
                      className='text-foreground size-4 opacity-60'
                      onClick={() => onSearchChange('')}
                    />
                  )
                }
                placeholder='Search by name...'
                className='text-foreground w-full'
              />
              {isSearching && (
                <Loader2 className='text-muted-foreground absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 animate-spin' />
              )}
            </div>
            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type='button'
                  className='text-foreground border-input bg-background hover:bg-accent hover:text-accent-foreground inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-medium whitespace-nowrap'>
                  <ArrowUpDown className='size-4' />
                  {!isMobile &&
                    (SORT_OPTIONS.find((opt) => opt.field === sortField)?.label || 'Sort')}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' sideOffset={4} className='z-50'>
                {SORT_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.field}
                    onClick={() => onSortChange(option.field, option.order)}
                    className={sortField === option.field ? 'bg-accent' : ''}>
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant={'outline'}
              onClick={onAddClick}
              className='gap-2'
              endIcon={<Plus className='h-4 w-4' />}>
              Add new
            </Button>
          </div>
        }
      />

      {/* Personnel List */}
      <PageMaxLayout dimensions={dimensions} isMobile={isMobile}>
        <div className='w-full space-y-3'>
          {/* Error State */}
          {error && (
            <div className='border-destructive/50 bg-destructive/10 flex flex-col items-center justify-center gap-4 rounded-lg border p-8 text-center'>
              <AlertCircle className='text-destructive h-10 w-10' />
              <div>
                <p className='text-destructive font-medium'>Error loading personnel</p>
                <p className='text-muted-foreground text-sm'>{error}</p>
              </div>
              <Button
                variant='outline'
                onClick={onRetry}
                startIcon={<RefreshCw className='size-4' />}>
                Retry
              </Button>
            </div>
          )}

          {/* Initial Loading State */}
          {isLoading && !error && (
            <div className='flex flex-col items-center justify-center gap-4 p-12'>
              <Loader2 className='text-primary h-10 w-10 animate-spin' />
              <p className='text-muted-foreground'>Loading personnel...</p>
            </div>
          )}

          {/* Personnel List */}
          {!isLoading && !error && (
            <>
              {personnel.map((person) => (
                <ListCard
                  key={person.id}
                  onClick={() => onRowClick(person.id)}
                  title={
                    <div className='flex space-x-3'>
                      <p className='font-bold'>{person.name}</p>
                    </div>
                  }
                  subTitle={
                    <div className='flex flex-wrap'>
                      <span>{groupNameMap.get(person.organization) || person.organization}</span>
                      {person.phone_number && (
                        <>
                          <Dot className='text-primary' />
                          <span>{person.phone_number}</span>
                        </>
                      )}
                      {person.email && (
                        <>
                          <Dot className='text-primary' />
                          <span>{person.email}</span>
                        </>
                      )}
                    </div>
                  }
                />
              ))}

              {/* Empty State */}
              {personnel.length === 0 && <EmptyState />}

              {/* Loading More Indicator */}
              {isLoadingMore && (
                <div className='flex items-center justify-center gap-2 py-4'>
                  <Loader2 className='text-primary h-5 w-5 animate-spin' />
                  <span className='text-muted-foreground text-sm'>Loading more...</span>
                </div>
              )}

              {/* Sentinel Element for Infinite Scroll */}
              {hasMore && !isLoadingMore && (
                <div ref={sentinelRef} className='h-1' aria-hidden='true' />
              )}
            </>
          )}
        </div>
      </PageMaxLayout>

      {/* Add/Edit Personnel Modal */}
      <PersonnelModal
        key={`${modalMode}-${selectedPersonnel?.id ?? 'new'}`}
        open={isModalOpen}
        onOpenChange={onModalClose}
        onSaveSuccess={onSaveSuccess}
        mode={modalMode}
        personnel={selectedPersonnel}
        isLoading={isLoadingPersonnel}
      />
    </div>
  );
};

export default PersonnelView;
